'use client';

import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { CheckCircle2, XCircle, ArrowRight, MessageSquare, Gauge, Zap } from 'lucide-react';
import Link from 'next/link';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';

export default function PlotsAlotVsPowerBIPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-yellow-600 bg-clip-text text-transparent">
              PlotsAlot vs Power BI
            </h1>
            <p className="text-xl text-muted-foreground mb-8">Power BI Alternative for Conversational Analytics</p>
            <p className="text-lg text-foreground/80 mb-8 leading-relaxed">
              Power BI is Microsoft's enterprise BI platform. PlotsAlot offers conversational data analysis with AI.
              Here's how they compare.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                href="/auth/signin"
                className="inline-flex items-center px-6 py-3 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors">
                Start Free Trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <Link
                href="/pricing"
                className="inline-flex items-center px-6 py-3 rounded-lg border border-border hover:bg-accent transition-colors">
                See Pricing
              </Link>
            </div>
          </motion.div>
        </section>

        {/* Key Differences */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card border border-border rounded-xl p-6">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <MessageSquare className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Chat with Your Data</h3>
              <p className="text-muted-foreground text-sm">
                Ask questions in plain English. No DAX formulas or complex data modeling required.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card border border-border rounded-xl p-6">
              <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-yellow-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Instant Analysis</h3>
              <p className="text-muted-foreground text-sm">
                Upload files and get insights immediately. No data modeling, relationships, or configuration.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-card border border-border rounded-xl p-6">
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-4">
                <Gauge className="h-6 w-6 text-blue-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Lower Learning Curve</h3>
              <p className="text-muted-foreground text-sm">
                Natural language interface means anyone can analyze data. No BI training required.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Pricing Comparison */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Real Pricing Comparison</h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Power BI Pricing */}
            <div className="bg-card border border-border rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-2">Microsoft Power BI</h3>
              <p className="text-3xl font-bold text-primary mb-4">$10-5,000/month</p>
              <p className="text-muted-foreground mb-6">
                Free: Desktop only. Pro: $10/user/mo. Premium: $20/user/mo. Premium Capacity: $4,995+/mo.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Free Power BI Desktop</p>
                    <p className="text-sm text-muted-foreground">Desktop app, no sharing</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Enterprise dashboards</p>
                    <p className="text-sm text-muted-foreground">Complex multi-source reports</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Microsoft ecosystem</p>
                    <p className="text-sm text-muted-foreground">Azure, SQL Server, Excel integration</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-red-500 mt-1">✗</div>
                  <div>
                    <p className="font-medium">Natural language queries</p>
                    <p className="text-sm text-muted-foreground">Requires DAX knowledge</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-red-500 mt-1">✗</div>
                  <div>
                    <p className="font-medium">Instant analysis</p>
                    <p className="text-sm text-muted-foreground">Requires data modeling setup</p>
                  </div>
                </div>
              </div>
            </div>

            {/* PlotsAlot Pricing */}
            <div className="bg-card border-2 border-primary rounded-xl p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-sm font-medium">
                Conversational BI
              </div>
              <h3 className="text-2xl font-bold mb-2">PlotsAlot</h3>
              <p className="text-3xl font-bold text-primary mb-4">$0-10/month</p>
              <p className="text-muted-foreground mb-6">Free: 3 messages. Pro: $10/mo (200 messages).</p>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Natural language interface</p>
                    <p className="text-sm text-muted-foreground">Chat with your data</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Instant insights</p>
                    <p className="text-sm text-muted-foreground">Upload and analyze immediately</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Python data science</p>
                    <p className="text-sm text-muted-foreground">pandas, plotly, scikit-learn</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Google Analytics integration</p>
                    <p className="text-sm text-muted-foreground">Direct OAuth connection</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">No data modeling needed</p>
                    <p className="text-sm text-muted-foreground">AI handles complexity</p>
                  </div>
                </div>
              </div>

              <Link
                href="/auth/signin"
                className="block w-full text-center py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </section>

        {/* Feature Comparison Table */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Full Feature Breakdown</h2>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-card rounded-xl overflow-hidden">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-4 font-semibold">Feature</th>
                  <th className="text-center p-4 font-semibold">PlotsAlot</th>
                  <th className="text-center p-4 font-semibold">Power BI</th>
                  <th className="text-left p-4 font-semibold">Winner & Why</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="p-4 font-medium">Learning Curve</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Low</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">High</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Natural language vs DAX formulas
                    and data modeling concepts.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Time to First Insight</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Minutes</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Hours-Days</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Upload and chat vs data modeling,
                    relationships, DAX, dashboard building.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Natural Language Queries</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Limited (Q&A)</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Core interface. Power BI Q&A is
                    limited and requires proper data model.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Data Modeling Required</td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - No modeling needed. Power BI
                    requires star schema, relationships, measures.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Python Code Execution</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Limited</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Full Python environment. Power BI
                    has Python visuals with limitations.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Real-time Dashboards</td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Power BI</span> - Live connections, streaming data,
                    auto-refresh dashboards.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Enterprise Data Sources</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Limited</span>
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Power BI</span> - 100+ data connectors (SQL Server,
                    Oracle, SAP, Salesforce, etc.).
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">File-based Analysis</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Tie</span> - Both support CSV, Excel. PlotsAlot adds
                    JSON, Parquet, PDF.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">DAX Formulas</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">N/A (Python)</span>
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Power BI</span> - Powerful formula language for
                    complex calculations.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Visualization Customization</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Power BI</span> - More visual customization options,
                    but requires manual configuration.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Cost for Small Teams</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">$0-49/mo</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">$10-20/user</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Flat rate vs per-user pricing.
                    5-person team: PlotsAlot $49 vs Power BI $50-100.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Google Analytics Integration</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Via connector</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Direct OAuth integration. Power
                    BI requires Google Analytics connector setup.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Row-Level Security</td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Power BI</span> - Enterprise security with RLS, data
                    governance features.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Mobile Apps</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Web only</span>
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Power BI</span> - Native iOS/Android apps with
                    offline mode.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Embedded Analytics</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Power BI</span> - More mature embedding options for
                    white-label scenarios.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Understanding the Difference */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Understanding the Difference</h2>

            <div className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-muted-foreground leading-relaxed mb-6">
                <strong className="text-foreground">
                  Power BI and PlotsAlot serve different segments of the analytics market
                </strong>
                .
              </p>

              <div className="bg-card border border-border rounded-xl p-6 mb-6">
                <h3 className="text-xl font-semibold mb-3">Power BI</h3>
                <p className="text-muted-foreground mb-0">
                  Power BI is an <strong className="text-foreground">enterprise business intelligence platform</strong>{' '}
                  designed for creating complex, multi-source dashboards and reports. It requires data modeling (star
                  schemas, relationships), DAX formulas for calculations, and significant training. Power BI excels at
                  connecting to dozens of enterprise data sources (SQL Server, Oracle, SAP, Salesforce) and creating
                  real-time dashboards for large organizations. It's built for IT teams and BI professionals.
                </p>
              </div>

              <div className="bg-card border border-border rounded-xl p-6 mb-6">
                <h3 className="text-xl font-semibold mb-3">PlotsAlot</h3>
                <p className="text-muted-foreground mb-0">
                  PlotsAlot is a <strong className="text-foreground">conversational analytics platform</strong> designed
                  for quick, exploratory analysis without technical setup. Upload files, connect Google Analytics, and
                  ask questions in natural language. No data modeling, no DAX, no training required. PlotsAlot is built
                  for non-technical users, small teams, and rapid insights. It trades enterprise features for simplicity
                  and speed.
                </p>
              </div>

              <p className="text-lg font-semibold text-foreground mt-8 mb-4">
                The key difference: Power BI is enterprise BI. PlotsAlot is conversational analytics.
              </p>
            </div>
          </div>
        </section>

        {/* When to Choose */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Choose PlotsAlot */}
            <div className="bg-card border-2 border-primary rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6">When to Choose PlotsAlot</h3>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">1. You Want Instant Analysis</h4>
                  <p className="text-sm text-muted-foreground">
                    Upload a CSV and get insights in minutes, not days. No data modeling or DAX required.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">2. Your Team Isn't Technical</h4>
                  <p className="text-sm text-muted-foreground">
                    Natural language means anyone can analyze data. No BI training, DAX courses, or data modeling
                    expertise needed.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">3. You're a Small Team or Startup</h4>
                  <p className="text-sm text-muted-foreground">
                    Flat-rate pricing ($0-49/mo) vs per-user fees. Better economics for small teams.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">4. You Work with Files, Not Databases</h4>
                  <p className="text-sm text-muted-foreground">
                    Analyze CSV exports, Excel spreadsheets, JSON data. Perfect for ad-hoc file-based analysis.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">5. You Need Google Analytics Integration</h4>
                  <p className="text-sm text-muted-foreground">
                    Direct OAuth connection to GA4. Simpler than Power BI's Google Analytics connector setup.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">6. You Want Python Data Science</h4>
                  <p className="text-sm text-muted-foreground">
                    Full Python environment with pandas, scikit-learn, plotly. More flexible than Power BI's limited
                    Python integration.
                  </p>
                </div>
              </div>
            </div>

            {/* Choose Power BI */}
            <div className="bg-card border border-border rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6">When to Stick with Power BI</h3>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">1. You Need Enterprise Dashboards</h4>
                  <p className="text-sm text-muted-foreground">
                    Real-time dashboards connecting multiple data sources with complex relationships. Power BI is
                    purpose-built for this.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">2. You're in the Microsoft Ecosystem</h4>
                  <p className="text-sm text-muted-foreground">
                    Power BI integrates seamlessly with Azure, SQL Server, SharePoint, Excel, Teams. Essential for
                    Microsoft-centric organizations.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">3. You Have Database Connections</h4>
                  <p className="text-sm text-muted-foreground">
                    Power BI connects to 100+ data sources including SQL Server, Oracle, SAP, Salesforce. Required for
                    enterprise data infrastructure.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">4. You Need Row-Level Security</h4>
                  <p className="text-sm text-muted-foreground">
                    Enterprise security features, data governance, and role-based access control. Critical for sensitive
                    data.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">5. You Want Complex DAX Calculations</h4>
                  <p className="text-sm text-muted-foreground">
                    Time intelligence, cumulative totals, complex business logic. DAX is more powerful than Python for
                    BI-specific calculations.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">6. You Have Existing Power BI Investment</h4>
                  <p className="text-sm text-muted-foreground">
                    Team trained in DAX, data models already built, dashboards deployed. Switching costs would be high.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Frequently Asked Questions</h2>

            <Accordion type="single" className="space-y-2">
              <AccordionItem value="item-1" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Can PlotsAlot replace Power BI for enterprises?</AccordionTrigger>
                <AccordionContent>
                  No. If you need real-time dashboards, multiple data source connections, row-level security, and
                  enterprise data governance, Power BI is essential. PlotsAlot is designed for quick, conversational
                  analysis, not enterprise BI infrastructure.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Is PlotsAlot easier to learn than Power BI?</AccordionTrigger>
                <AccordionContent>
                  Yes, significantly. PlotsAlot uses natural language - anyone can ask questions immediately. Power BI
                  requires understanding data modeling, relationships, and DAX formulas. Learning curve is
                  weeks-to-months for Power BI vs minutes for PlotsAlot.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Which is better for small teams?</AccordionTrigger>
                <AccordionContent>
                  PlotsAlot. Flat-rate pricing ($0-49/mo) beats per-user fees for teams under 5-10 people. Lower
                  learning curve means faster time-to-value. Power BI makes more sense for teams with BI expertise and
                  enterprise data needs.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Can I use both Power BI and PlotsAlot?</AccordionTrigger>
                <AccordionContent>
                  Yes! Many teams use Power BI for enterprise dashboards and PlotsAlot for ad-hoc analysis. Use Power BI
                  for production reporting, PlotsAlot for exploratory work and quick insights from CSV exports.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Does PlotsAlot connect to databases like Power BI?</AccordionTrigger>
                <AccordionContent>
                  Not currently. PlotsAlot works with file uploads (CSV, Excel, JSON, Parquet) and Google Analytics
                  integration. Power BI's 100+ database connectors are essential for enterprise data infrastructure.
                  Feature roadmap includes database connections.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Which creates better visualizations?</AccordionTrigger>
                <AccordionContent>
                  Both create high-quality charts. Power BI offers more customization options and interactive features
                  for dashboards. PlotsAlot generates modern, publication-quality charts instantly via conversation.
                  Different strengths for different needs.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>What about Tableau vs PlotsAlot?</AccordionTrigger>
                <AccordionContent>
                  Similar comparison to Power BI. Tableau is enterprise BI with drag-and-drop interface (more intuitive
                  than Power BI but still complex). PlotsAlot is conversational analytics - different paradigm. Check
                  our Tableau comparison for details.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-primary/10 to-yellow-500/10 rounded-2xl p-12 border border-primary/20">
            <h2 className="text-3xl font-bold mb-4">Skip the Complexity, Get Insights Faster</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Try PlotsAlot free. No data modeling, no DAX, no training. Just upload and chat.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                href="/auth/signin"
                className="inline-flex items-center px-8 py-4 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors text-lg font-semibold">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                href="/pricing"
                className="inline-flex items-center px-8 py-4 rounded-lg border-2 border-primary hover:bg-primary/5 transition-colors text-lg font-semibold">
                View Pricing
              </Link>
            </div>
            <p className="text-sm text-muted-foreground mt-6">
              Free tier • No credit card required • No data modeling needed
            </p>
          </div>
        </section>

        {/* Other Comparisons */}
        <section className="container mx-auto px-4 md:px-8 mt-16">
          <h3 className="text-2xl font-bold mb-6 text-center">Other Comparisons</h3>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link
              href="/compare/gamma"
              className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors">
              <h4 className="font-semibold mb-2">vs Gamma</h4>
              <p className="text-sm text-muted-foreground">AI presentation tool vs data analysis platform</p>
            </Link>
            <Link
              href="/compare/chatgpt"
              className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors">
              <h4 className="font-semibold mb-2">vs ChatGPT</h4>
              <p className="text-sm text-muted-foreground">General AI vs specialized data analysis</p>
            </Link>
            <Link
              href="/compare/excel"
              className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors">
              <h4 className="font-semibold mb-2">vs Excel</h4>
              <p className="text-sm text-muted-foreground">Traditional spreadsheets vs AI-powered analysis</p>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
